import React from 'react';
import { t } from '../i18n';
import { makeMatcher } from '../utils/search';

// ─── Выбор из длинного справочника с поиском ─────────────────────────────────
//
// Обычный <select> годится, пока строк десяток. В плёнке их 152, в ДСП 46, и
// нужную приходится искать прокруткой — глазами, по списку, где половина
// названий начинается одинаково. Здесь то же поле, но сверху строка поиска:
// набрал «дуб вотан» — осталось две строки.
//
// Поиск по вхождению и без учёта регистра: «шимо» должно находить «Ясень Шимо
// Светлый», а не только то, что начинается с «шимо».
//
// options: [{ id, name }]
// suggested: те же объекты — их показываем первыми, под своей подписью:
//   подсказка теряет смысл, если лежит вперемешку со всем справочником.
export default function SearchSelect({
  options, value, onChange, placeholder, suggested, suggestedLabel, disabled,
}) {
  const [q, setQ] = React.useState('');
  // Выбранный завод. Технолог пишет рецепт на плиту КОНКРЕТНОГО завода:
  // у соседнего та же толщина, но другая плотность и другой режим пресса.
  const [maker, setMaker] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const boxRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const selected = options.find(o => String(o.id) === String(value));

  React.useEffect(() => {
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  // Открыли — курсор сразу в поиске: иначе первое действие всегда лишний клик
  React.useEffect(() => {
    if (open) setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
  }, [open]);

  const needle = q.trim().toLowerCase();
  // Ищем и по заводу с кодом: одна и та же плита лежит разными позициями от
  // разных заводов, и «кроношпан» — такой же способ сузить список, как «дуб».
  const match = (o) => makeMatcher(needle)(o.name, o.manufacturer, o.code_1c);

  // Заводы, какие есть среди этих материалов. Один — отбирать нечего.
  const makers = [...new Set((options || [])
    .map(o => o.manufacturer).filter(Boolean))].sort();
  const byMaker = (o) => !maker || o.manufacturer === maker;

  const sugg = (suggested || []).filter(o => match(o) && byMaker(o));
  const suggIds = new Set(sugg.map(o => String(o.id)));
  const rest = options.filter(
    o => match(o) && byMaker(o) && !suggIds.has(String(o.id)));

  const pick = (id) => { onChange(id); setOpen(false); setQ(''); };

  const FIELD = {
    width: '100%', minWidth: 0, textAlign: 'left', cursor: disabled ? 'default' : 'pointer',
    background: 'var(--input)', border: '1px solid var(--border)', borderRadius: 8,
    color: selected ? 'var(--text)' : 'var(--dim)', padding: '10px 12px',
    fontSize: 13.5, fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', gap: 8,
  };
  const ROW = {
    display: 'block', width: '100%', textAlign: 'left', background: 'none',
    border: 'none', borderRadius: 6, color: 'var(--text)', padding: '8px 10px',
    fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  };
  // Завод — приглушённым значком следом за названием, а не вместо кода:
  // раньше он приезжал из 1С прямо в поле артикула и ломал ровный столбец.
  const MAKER = {
    background: 'var(--input)', border: '1px solid var(--border)',
    borderRadius: 5, padding: '0 6px', marginLeft: 8,
    fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap',
  };
  const CAP = {
    padding: '8px 10px 4px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em',
    textTransform: 'uppercase', color: 'var(--dim)',
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <button type="button" disabled={disabled} onClick={() => setOpen(o => !o)} style={FIELD}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.name : (placeholder || t('— выбрать —'))}
          {selected && selected.manufacturer
            ? <span style={MAKER}>{selected.manufacturer}</span> : null}
        </span>
        <span style={{ color: 'var(--dim)', fontSize: 11 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', zIndex: 60, top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: 'var(--shadow)', padding: 8, maxHeight: 320, overflowY: 'auto',
        }}>
          <input
            ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('Поиск…')}
            style={{
              width: '100%', background: 'var(--input)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text)', padding: '8px 10px', fontSize: 13,
              fontFamily: 'inherit', marginBottom: 6, boxSizing: 'border-box',
            }} />

          {makers.length > 1 && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap',
                          marginBottom: 6 }}>
              {['', ...makers].map(m => (
                <button key={m || 'all'} type="button" onClick={() => setMaker(m)}
                  style={{
                    background: maker === m ? 'var(--accent-weak)' : 'var(--input)',
                    border: '1px solid var(--border)', borderRadius: 6,
                    color: maker === m ? 'var(--text)' : 'var(--muted)',
                    padding: '3px 9px', fontSize: 11.5,
                    fontWeight: maker === m ? 700 : 500,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                  {m || t('Все заводы')}
                </button>
              ))}
            </div>
          )}

          {sugg.length > 0 && (
            <>
              <div style={CAP}>{suggestedLabel || t('Подходит')}</div>
              {sugg.map(o => (
                <button key={'s' + o.id} type="button" onClick={() => pick(o.id)}
                  style={{ ...ROW, background: String(o.id) === String(value) ? 'var(--accent-weak)' : 'none' }}>
                  {o.name}{o.manufacturer ? <span style={MAKER}>{o.manufacturer}</span> : null}
                </button>
              ))}
              {rest.length > 0 && <div style={CAP}>{t('Остальные')}</div>}
            </>
          )}

          {rest.map(o => (
            <button key={o.id} type="button" onClick={() => pick(o.id)}
              style={{ ...ROW, background: String(o.id) === String(value) ? 'var(--accent-weak)' : 'none' }}>
              {o.name}{o.manufacturer ? <span style={MAKER}>{o.manufacturer}</span> : null}
            </button>
          ))}

          {sugg.length === 0 && rest.length === 0 && (
            <div style={{ color: 'var(--dim)', fontSize: 13, padding: '10px' }}>
              {t('Ничего не найдено')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
